import {
    analyzeImageFromArrayBuffer,
    type FocusedMeta,
    type ProcessedImage,
} from "@nova-rnd/sensortool-wasm"
import { defineTask, type TaskResult } from "@nova/webworker-pool"
import { initializeSensorToolFromModule } from "integrations/sensortool"

const readMetadata = async (processedImage: ProcessedImage) => {
    const meta: FocusedMeta | undefined = await processedImage.getFocusedMeta()
    if (!meta) return undefined
    return {
        gps: {
            latitude: meta.gps.latitude,
            longitude: meta.gps.longitude,
            altitude: meta.gps.altitude,
        },
        pyr: { pitch: meta.pyr.pitch, roll: meta.pyr.roll, yaw: meta.pyr.yaw },
        height: meta.height,
        focalLength: meta.focalLength,
        isSupportedSensor: meta.isSupportedSensor,
        takenAt: meta.takenAt,
        warnings: processedImage.warnings as Record<string, unknown>,
        tags: processedImage.tags as Record<string, Record<string, unknown>>,
        make: meta.make,
        model: meta.model,
        fov: meta.fov,
        imageWidth: meta.imageWidth,
        imageHeight: meta.imageHeight,
        hasThermalData: meta.hasThermalData,
    }
}

export const processImage = defineTask({
    name: "processImage",
    wasm: ["sensortool"],
    run: async (
        { fileBuffer, xmlFileBuffer }: { fileBuffer: ArrayBuffer; xmlFileBuffer?: ArrayBuffer },
        { wasm }
    ) => {
        initializeSensorToolFromModule(wasm.sensortool)
        let processedImage: ProcessedImage | undefined
        try {
            processedImage = await analyzeImageFromArrayBuffer(fileBuffer)
            if (xmlFileBuffer) processedImage.addTelemetryFromArrayBuffer(xmlFileBuffer)
            return { data: await readMetadata(processedImage) }
        } catch (error) {
            console.warn("processImage: failed to read image metadata", error)
            return { data: undefined }
        } finally {
            try {
                processedImage?.free()
            } catch (error) {
                console.warn("processImage: failed to free processed image", error)
            }
        }
    },
})

export type ImageMetadata = TaskResult<typeof processImage>
